import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, ImagePlus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const SECTORS = [
  "Agriculture", "Technology", "Retail & Trade", "Food & Hospitality",
  "Health & Wellness", "Education", "Finance & Legal",
  "Construction & Real Estate", "Creative & Media", "Fashion & Beauty",
  "Manufacturing", "Transport & Logistics", "Professional Services", "Other",
];

const EMPTY = {
  owner_name: "", business_name: "", sector: "", phone: "", email: "",
  description: "", location: "", website: "", social: "", offerings: "", needs: "",
};

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55";

const Field = ({ label, id, required, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label} {required && <span className="text-gold-dark">*</span>}
    </label>
    {children}
  </div>
);

const SectionTitle = ({ n, children }) => (
  <div className="flex items-baseline gap-4 border-b border-stone_warm pb-4">
    <span className="font-serif text-2xl font-light text-gold">{n}</span>
    <h3 className="font-serif text-xl font-medium tracking-tight text-charcoal">{children}</h3>
  </div>
);

export const RegisterForm = ({ onRegistered }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd);
      setLogo({ path: res.data.path, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not upload the image."));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => setLogo(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/businesses", { ...form, logo_path: logo?.path || "" });
      setConfirmation({
        name: res.data.business_name,
        link: `${window.location.origin}/edit/${res.data.edit_token}`,
        approved: res.data.approved,
      });
      toast.success(res.data.approved
        ? "Welcome to the Guild — your business is now listed in the directory."
        : "Registration received — it will appear once approved by the Guild office.");
      setForm(EMPTY);
      setLogo(null);
      onRegistered?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: EASE }}
      className="space-y-12 rounded-2xl border border-stone_warm bg-white p-8 shadow-[0_8px_30px_rgba(26,26,26,0.04)] sm:p-12"
      data-testid="registration-form"
    >
      {confirmation && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="rounded-2xl border border-gold/50 bg-[#F3F0EB] p-7"
          data-testid="registration-confirmation"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Registration received</p>
          <h3 className="mt-2 font-serif text-2xl font-medium tracking-tight text-charcoal">
            {confirmation.name} {confirmation.approved ? "is now live in the directory." : "awaits approval by the Guild office."}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-charcoal/65">
            Save your private edit link — it lets you update your listing at any time, no login needed.
            Anyone with this link can edit your listing, so keep it safe.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <code className="max-w-full truncate rounded-lg border border-stone_warm bg-white px-3 py-2 font-mono text-[11px] text-charcoal/70" data-testid="confirmation-edit-link">
              {confirmation.link}
            </code>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(confirmation.link);
                  toast.success("Edit link copied — keep it somewhere safe.");
                } catch {
                  toast.error("Could not copy — please copy the link manually.");
                }
              }}
              className="rounded-full border border-charcoal/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              data-testid="confirmation-copy-link-button"
            >
              Copy link
            </button>
            <button
              type="button"
              onClick={() => setConfirmation(null)}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/45 underline-offset-4 transition-colors hover:text-gold-dark hover:underline"
              data-testid="confirmation-dismiss-button"
            >
              Dismiss
            </button>
          </div>
        </motion.div>
      )}
      <div className="space-y-6">
        <SectionTitle n="I.">Essentials</SectionTitle>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field label="Your full name" id="owner_name" required>
            <input id="owner_name" required value={form.owner_name} onChange={set("owner_name")} className={inputCls} placeholder="e.g. Mary Joseph" data-testid="register-owner-name-input" />
          </Field>
          <Field label="Business name" id="business_name" required>
            <input id="business_name" required value={form.business_name} onChange={set("business_name")} className={inputCls} placeholder="e.g. Mustard Seed Interiors" data-testid="register-business-name-input" />
          </Field>
          <Field label="Business sector" id="sector" required>
            <select id="sector" required value={form.sector} onChange={set("sector")} className={inputCls} data-testid="register-sector-select">
              <option value="" disabled>Select a sector</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Phone" id="phone" required>
            <input id="phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 555 000 0000" data-testid="register-phone-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Email" id="email" required>
              <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@yourbusiness.com" data-testid="register-email-input" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="About your business" id="description" required>
              <textarea id="description" required rows={3} value={form.description} onChange={set("description")} className={inputCls} placeholder="Tell the Guild what you do and why you do it." data-testid="register-description-input" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <span className={labelCls}>
              Logo or photo <span className="normal-case tracking-normal text-charcoal/35">(optional, max 5MB)</span>
            </span>
            <div className="flex items-center gap-5">
              {logo ? (
                <img src={logo.preview} alt="Logo preview" className="h-16 w-16 rounded-full border border-stone_warm object-cover" data-testid="register-logo-preview" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-stone_warm bg-[#F3F0EB] text-charcoal/35">
                  <ImagePlus size={18} />
                </span>
              )}
              <label className="cursor-pointer rounded-full border border-charcoal/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark" data-testid="register-logo-label">
                {uploading ? "Uploading…" : logo ? "Replace image" : "Choose image"}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploading} data-testid="register-logo-input" />
              </label>
              {logo && (
                <button type="button" onClick={removeLogo} className="text-charcoal/40 transition-colors hover:text-red-600" data-testid="register-logo-remove" aria-label="Remove logo">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <SectionTitle n="II.">Location & links</SectionTitle>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field label="Location / area" id="location" required>
            <input id="location" required value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Downtown Calgary" data-testid="register-location-input" />
          </Field>
          <Field label="Website" id="website">
            <input id="website" value={form.website} onChange={set("website")} className={inputCls} placeholder="https://…" data-testid="register-website-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Social handle" id="social">
              <input id="social" value={form.social} onChange={set("social")} className={inputCls} placeholder="@yourbusiness" data-testid="register-social-input" />
            </Field>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <SectionTitle n="III.">Offerings & needs</SectionTitle>
        <Field label="Products & services you offer" id="offerings" required>
          <textarea id="offerings" required rows={3} value={form.offerings} onChange={set("offerings")} className={inputCls} placeholder="List what other members can buy, book, or commission from you." data-testid="register-offerings-input" />
        </Field>
        <Field label="What you need help with" id="needs">
          <textarea id="needs" rows={2} value={form.needs} onChange={set("needs")} className={inputCls} placeholder="Suppliers, referrals, partnerships — let the Guild know." data-testid="register-needs-input" />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting || uploading}
        className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        data-testid="register-submit-button"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />}
        {submitting ? "Registering…" : "Submit registration"}
      </button>
    </motion.form>
  );
};
